import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import KanbanCard from './KanbanCard';

const KanbanColumn = ({ status, title, workOrders, onCardClick, onQuickView, canViewCosts, canMove }) => {
    // Same sessionStorage-per-column scroll restoration pattern as the Support
    // Kanban board (src/app/support/index.js's StatusColumn) — without it, a
    // scrolled column resets to the top every time this component remounts,
    // which happens whenever you navigate away (e.g. to a work order's detail
    // page) and back, since the board unmounts fully on route change.
    const scrollContainerRef = useRef(null);
    const hasRestoredScroll = useRef(false);
    const scrollSaveFrame = useRef(null);

    useLayoutEffect(() => {
        if (hasRestoredScroll.current || !scrollContainerRef.current) return;
        const savedScrollTop = sessionStorage.getItem(`maintenance-kanban-scroll-${status}`);
        if (savedScrollTop) {
            scrollContainerRef.current.scrollTop = parseInt(savedScrollTop, 10);
        }
        hasRestoredScroll.current = true;
    }, [status]);

    useEffect(() => () => {
        if (scrollSaveFrame.current) cancelAnimationFrame(scrollSaveFrame.current);
    }, []);

    // Coalesce rapid scroll events to one write per frame instead of one per event
    const handleColumnScroll = (e) => {
        const scrollTop = e.currentTarget.scrollTop;
        if (scrollSaveFrame.current) cancelAnimationFrame(scrollSaveFrame.current);
        scrollSaveFrame.current = requestAnimationFrame(() => {
            sessionStorage.setItem(`maintenance-kanban-scroll-${status}`, scrollTop.toString());
        });
    };

    return (
        <div className="flex max-h-full w-[290px] shrink-0 flex-col rounded-2xl bg-[#F6F4F0] p-3 shadow-sm border border-inv-cream-200">
            <div className="mb-4 flex items-center justify-between px-2 pt-2">
                <h3 className="text-[15px] font-bold text-inv-ink">{title}</h3>
                <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-white px-2 text-xs font-bold text-[#8C8175] shadow-sm">
                    {workOrders.length}
                </span>
            </div>

            <Droppable droppableId={status}>
                {(provided, snapshot) => (
                    <div
                        ref={(node) => { provided.innerRef(node); scrollContainerRef.current = node; }}
                        onScroll={handleColumnScroll}
                        className={`flex-1 overflow-y-auto rounded-xl p-1 transition-colors custom-scrollbar ${
                            snapshot.isDraggingOver ? 'bg-inv-cream-100' : ''
                        }`}
                        {...provided.droppableProps}
                    >
                        {workOrders.map((workOrder, index) => (
                            <KanbanCard
                                key={workOrder._id}
                                workOrder={workOrder}
                                index={index}
                                onClick={() => onCardClick(workOrder)}
                                onQuickView={onQuickView}
                                canViewCosts={canViewCosts}
                                canMove={canMove}
                            />
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </div>
    );
};

export default KanbanColumn;
